import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { createClient, LDClient, LDFlagSet, LDFlagValue, LDSingleKindContext } from '@launchdarkly/js-client-sdk';
import { BehaviorSubject } from 'rxjs';

import { JwtInfo } from '@hsc/common/schemas/eua.schema.js';
import { normalizeJwtContext } from '@hsc/common/utilities/jwt-utils.js';
import { environment } from '@environments/environment';

import { SplunkLoggerService } from './splunk-logger.service';

/**
 * https://github.com/Nationwide/launch-darkly-onboarding
 */
@Injectable({ providedIn: 'root' })
export class LaunchDarklyService {
  private readonly splunkLoggerService = inject(SplunkLoggerService);
  private ldClient: LDClient | null = null;
  private readonly flagsSubject = new BehaviorSubject<LDFlagSet>({});

  // Emits current flag values whenever they change to subscribers
  readonly flags$ = this.flagsSubject.asObservable();
  // Provide a reactive way to access the current flag values as a Signal
  private readonly flags: Signal<LDFlagSet> = toSignal(this.flags$, { initialValue: {} });

  /**
   * Initializes the LaunchDarkly client and loads initial flags.
   * @throws Error If client-side config is missing or client initialization fails.
   */
  async initialize(): Promise<void> {
    const clientSideId = environment.LAUNCH_DARKLY_CLIENT_SIDE_ID;
    if (!clientSideId) {
      this.splunkLoggerService.error('Missing Launch Darkly client-side ID');
      throw new Error('Failed to load Launch Darkly config');
    }

    // Clean up previous client if re-initializing
    if (this.ldClient) await this.ldClient.close();

    const user: LDSingleKindContext = {
      kind: 'user',
      key: 'anonymous-user',
      anonymous: true,
    };

    this.ldClient = createClient(clientSideId, user, {
      logger: {
        // Drop debug/info logs to avoid cluttering the console, but still log warnings and errors
        debug: () => null,
        info: () => null,
        warn: console.warn,
        error: console.error,
      },
    });

    void this.ldClient.start();

    try {
      const initResult = await this.ldClient.waitForInitialization({ timeout: 10 }); // Wait 10 seconds for initialization

      if (initResult.status === 'complete') {
        this.splunkLoggerService.info('LaunchDarkly initialization complete');
      } else if (initResult.status === 'timeout') {
        const error = new Error('LaunchDarkly initialization timed out');
        this.splunkLoggerService.error(error, { message: 'LaunchDarkly initialization timed out' });
      } else {
        // Throw, let the parent catch handle this instead of duplicating work.
        throw initResult.error;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.splunkLoggerService.error(error, { message: 'LaunchDarkly initialization failed' });
    }

    this.flagsSubject.next(this.ldClient.allFlags());

    this.ldClient.on('change', () => {
      this.flagsSubject.next(this.ldClient!.allFlags());
    });
  }

  /** Returns a one-time snapshot of a flag's current value or the provided default. */
  getFlagValue(flagKey: string, defaultValue: LDFlagValue): LDFlagValue {
    if (!this.ldClient) {
      // LD should be initialized or we fail app startup
      this.splunkLoggerService.error('LaunchDarkly client not initialized');
      return defaultValue;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const flagValue = this.ldClient.variation(flagKey, defaultValue);
    this.splunkLoggerService.info(`LaunchDarkly flag value for '${flagKey}':`, { flagValue });
    return flagValue;
  }

  /** Returns a reactive Signal that updates when a flag's value changes or the provided default. */
  getFlagSignal<T>(flagKey: string, defaultValue: T): Signal<T> {
    return computed(() => (this.flags()[flagKey] as T) ?? defaultValue);
  }

  /** Identifies the active user for LaunchDarkly targeting. */
  identifyUser(jwtInfo: JwtInfo): void {
    if (!this.ldClient) {
      this.splunkLoggerService.warn('LaunchDarkly client not initialized');
      return;
    }

    const launchDarklyVars = normalizeJwtContext(jwtInfo);

    const user: LDSingleKindContext = {
      kind: 'user',
      key: jwtInfo.userId.toLowerCase().trim(),
      ...launchDarklyVars,
    };

    void this.ldClient.identify(user);
    this.splunkLoggerService.info('LaunchDarkly user identified');
  }
}
